import type { ReferenceType } from '@cult/domain';
import {
  type ReferenceCreateInput,
  type ReferenceItem,
  ReferenceItemSchema,
  ReferenceListResponseSchema,
} from '@cult/schemas';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../../../../lib/api-client';
import { referenceKeys } from '../../../reference';
import { adminKeys } from '../../admin-keys';
import type { ReferenceHooks, ReferenceUpdateVars } from '../types';

const JSON_HEADERS = { 'Content-Type': 'application/json' };
const STALE_TIME = 30_000;

/** The admin list and mutation hooks for one reference type, backed by /admin/reference/<type>. */
export const createReferenceHooks = (type: ReferenceType): ReferenceHooks => {
  const basePath = `/admin/reference/${type}`;
  const listQueryKey = adminKeys.reference(type);
  // Mutations refresh the admin list and the public list the forms and filters read.
  const invalidateKeys = [listQueryKey, referenceKeys.list(type)];

  const invalidateAll = (queryClient: ReturnType<typeof useQueryClient>) => {
    for (const queryKey of invalidateKeys) queryClient.invalidateQueries({ queryKey });
  };

  const useList: ReferenceHooks['useList'] = () =>
    useQuery<ReferenceItem[]>({
      queryKey: listQueryKey,
      queryFn: async () => ReferenceListResponseSchema.parse(await apiFetch<unknown>(basePath)).items,
      staleTime: STALE_TIME,
    });

  const useCreate: ReferenceHooks['useCreate'] = () => {
    const queryClient = useQueryClient();
    return useMutation<ReferenceItem, Error, ReferenceCreateInput>({
      mutationFn: async (input) =>
        ReferenceItemSchema.parse(
          await apiFetch<unknown>(basePath, {
            method: 'POST',
            headers: JSON_HEADERS,
            body: JSON.stringify(input),
          }),
        ),
      onSuccess: () => invalidateAll(queryClient),
    });
  };

  const useUpdate: ReferenceHooks['useUpdate'] = () => {
    const queryClient = useQueryClient();
    return useMutation<ReferenceItem, Error, ReferenceUpdateVars>({
      mutationFn: async ({ id, patch }) =>
        ReferenceItemSchema.parse(
          await apiFetch<unknown>(`${basePath}/${id}`, {
            method: 'PATCH',
            headers: JSON_HEADERS,
            body: JSON.stringify(patch),
          }),
        ),
      onSuccess: () => invalidateAll(queryClient),
    });
  };

  return { useList, useCreate, useUpdate };
};
